import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsDesktop, useIsMobile } from "@/hooks/useBreakpoint";
import CommandPalette from "@/components/CommandPalette";
import QuickActionFab from "@/components/QuickActionFab";
import SidebarInner from "@/components/SidebarInner";
import ProductBrand from "@/components/ProductBrand";
import MobileDrawer from "@/components/MobileDrawer";
import BottomNav from "@/components/BottomNav";
import NotificationBell from "@/components/NotificationBell";
import { ChatLauncher } from "@/chat/ChatExperience";
import PWAInstallButton from "@/components/PWAInstallButton";
import { crumbsFor } from "@/navigation";
import { Search } from "lucide-react";

// --------------------------------------------------------------------------
export default function Layout({ children }) {
  const loc      = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("flg-sidebar-collapsed") === "1"
  );
  const [cmdOpen, setCmdOpen]     = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dynamicCrumbs, setDynamicCrumbs] = useState(null);

  const isDesktop = useIsDesktop();   // ≥ 1024px
  const isMobile  = useIsMobile();    // < 768px

  // Persist sidebar collapse state
  useEffect(() => {
    localStorage.setItem("flg-sidebar-collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  // ⌘K / Ctrl+K  →  open command palette
  // ⌘B / Ctrl+B  →  toggle sidebar collapse (desktop only)
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCmdOpen(true);
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === "b" || e.key === "B")) {
        e.preventDefault();
        setCollapsed(c => !c);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // Close mobile drawer automatically on any route change
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  useEffect(() => {
    setDynamicCrumbs(null);
    const updateBreadcrumb = (event) => {
      if (
        event.detail?.pathname === loc.pathname
        && Array.isArray(event.detail?.crumbs)
        && event.detail.crumbs.length
      ) {
        setDynamicCrumbs(event.detail.crumbs);
      }
    };
    window.addEventListener("vianbills:breadcrumb", updateBreadcrumb);
    return () => window.removeEventListener("vianbills:breadcrumb", updateBreadcrumb);
  }, [loc.pathname]);

  // Handle navigation messages from the service worker (Web Push notification clicks).
  // When the user taps a system notification, the SW focuses the app window and
  // posts { type: "navigate", url: "/invoices/123" } so the React router deep-links
  // to the relevant document without a full page reload.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === "open-notifications") {
        const params = new URLSearchParams(window.location.search);
        params.set("notifications", "open");
        navigate(`${window.location.pathname}?${params}${window.location.hash}`);
        return;
      }
      if (event.data?.type === "navigate" && event.data?.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [navigate]);

  const crumbs = dynamicCrumbs || crumbsFor(loc.pathname);

  // ─── Mobile / Tablet layout  (<1024px) ───────────────────────────────────
  if (!isDesktop) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100dvh", background: "var(--flg-bg)" }}>

        {/* ── Topbar ──────────────────────────────────────────────── */}
        <header
          data-testid="topbar"
          style={{
            height: isMobile ? "var(--flg-topbar-h-mobile)" : "var(--flg-topbar-h)",
            display: "flex", alignItems: "center",
            gap: 10, padding: "0 14px",
            background: "var(--flg-glass-bg-strong)",
            backdropFilter: "var(--flg-glass-blur)",
            WebkitBackdropFilter: "var(--flg-glass-blur)",
            borderBottom: "1px solid var(--flg-border)",
            position: "sticky", top: 0,
            zIndex: "var(--flg-z-sticky)",
            flexShrink: 0,
          }}
        >
          {/* Hamburger */}
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            data-testid="mobile-hamburger"
            style={{
              width: 38, height: 38, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "transparent", border: "none", cursor: "pointer",
              color: "var(--flg-fg-secondary)", borderRadius: 8,
            }}
          >
            {/* Inline SVG avoids an extra import */}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"
              stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
              <line x1="3" y1="5"  x2="17" y2="5"  />
              <line x1="3" y1="10" x2="17" y2="10" />
              <line x1="3" y1="15" x2="17" y2="15" />
            </svg>
          </button>

          {/* Brand name (mobile) or breadcrumbs (tablet) */}
          {isMobile ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0, overflow: "hidden" }}>
              <ProductBrand width={100} />
            </div>
          ) : (
            /* Tablet: show breadcrumbs */
            <div className="f-breadcrumb" style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              {crumbs.map((c, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span style={{ color: "var(--flg-fg-subtle)", margin: "0 2px" }}>/</span>}
                  <span
                    className={i === crumbs.length - 1 ? "f-breadcrumb__current" : "f-breadcrumb__crumb"}
                    style={{ whiteSpace: "nowrap" }}
                  >{c}</span>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Search icon + install + notifications */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <QuickActionFab placement="header" />
            <button
              onClick={() => setCmdOpen(true)}
              aria-label="Search (⌘K)"
              data-testid="topbar-cmdk"
              style={{
                width: 36, height: 36,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "var(--flg-bg-elevated)",
                border: "1px solid var(--flg-border)",
                borderRadius: 8, cursor: "pointer",
                color: "var(--flg-fg-muted)",
              }}
            >
              <Search size={15} />
            </button>
            <PWAInstallButton compact />
            <ChatLauncher />
            <NotificationBell />
          </div>
        </header>

        {/* ── Page content ────────────────────────────────────────── */}
        <main
          data-testid="main-content"
          style={{
            flex: 1, minWidth: 0, minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            // Reserve space under the fixed bottom nav, and under the floating quick-action
            // button, so pagination/save controls at the bottom of a page are never hidden.
            paddingBottom: isMobile
              ? "calc(var(--flg-bottom-nav-h-safe) + var(--flg-fab-clearance-mobile-extra))"
              : "var(--flg-fab-clearance)",
          }}
        >
          {children}
        </main>

        {/* ── Bottom navigation (mobile only) ─────────────────────── */}
        {isMobile && (
          <BottomNav onMoreClick={() => setDrawerOpen(true)} />
        )}

        {/* ── Slide-in drawer (mobile + tablet) ───────────────────── */}
        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpenCmdPalette={() => { setDrawerOpen(false); setCmdOpen(true); }}
        />

        {/* ── Shared overlays ─────────────────────────────────────── */}
        <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
      </div>
    );
  }

  // ─── Desktop layout  (≥1024px) ─── FROZEN — DO NOT MODIFY ───────────────
  return (
    <div className="f-app" style={{ display: "flex", minHeight: "100dvh", background: "var(--flg-bg)" }}>

      {/* ============== SIDEBAR ============== */}
      <aside
        data-testid="sidebar"
        style={{
          width: collapsed ? "var(--flg-sidebar-w-collapsed)" : "var(--flg-sidebar-w)",
          background: "var(--flg-bg-elevated)",
          borderRight: "1px solid var(--flg-border)",
          display: "flex", flexDirection: "column",
          position: "sticky", top: 0, height: "100vh",
          transition: "width 220ms cubic-bezier(0.4, 0, 0.2, 1)",
          flexShrink: 0, overflow: "hidden",
        }}
      >
        <SidebarInner
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          showCollapseButton={true}
          onOpenCmdPalette={() => setCmdOpen(true)}
        />
      </aside>

      {/* ============== MAIN AREA ============== */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>

        {/* Top bar */}
        <header
          className="f-topbar"
          data-testid="topbar"
          style={{
            height: "var(--flg-topbar-h)",
            display: "flex", alignItems: "center",
            gap: 16, padding: "0 24px",
            background: "var(--flg-glass-bg-strong)",
            backdropFilter: "var(--flg-glass-blur)",
            WebkitBackdropFilter: "var(--flg-glass-blur)",
            borderBottom: "1px solid var(--flg-border)",
            position: "sticky", top: 0, zIndex: "var(--flg-z-sticky)",
          }}
        >
          {/* Breadcrumb */}
          <div className="f-breadcrumb" data-testid="topbar-breadcrumb">
            {crumbs.map((c, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ color: "var(--flg-fg-subtle)", margin: "0 2px" }}>/</span>}
                <span className={i === crumbs.length - 1 ? "f-breadcrumb__current" : "f-breadcrumb__crumb"}>
                  {c}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* Right cluster */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            <QuickActionFab placement="header" />
            <button
              onClick={() => setCmdOpen(true)}
              data-testid="topbar-cmdk"
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "6px 12px 6px 10px",
                height: 34, minWidth: 260,
                background: "var(--flg-bg-elevated)",
                border: "1px solid var(--flg-border)",
                borderRadius: 8,
                cursor: "pointer",
                color: "var(--flg-fg-muted)",
                fontSize: 13,
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "var(--flg-border-strong)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "var(--flg-border)"}
            >
              <Search size={13} />
              <span style={{ flex: 1, textAlign: "left" }}>Search anything…</span>
              <span className="f-kbd" style={{ fontSize: 10 }}>⌘K</span>
            </button>
            <PWAInstallButton />
            <ChatLauncher />
            <NotificationBell />
          </div>
        </header>

        {/* Content */}
        <main
          style={{
            flex: 1, minWidth: 0, minHeight: 0,
            // Reserve space under the floating quick-action button so pagination/save
            // controls at the bottom of a page are never hidden beneath it.
            paddingBottom: "var(--flg-fab-clearance)",
          }}
          data-testid="main-content"
        >
          {children}
        </main>
      </div>

      {/* Command palette */}
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </div>
  );
}
